import { useRef, useState } from 'react';
import { Button, FormControl, FormErrorMessage, FormLabel, Text, VStack } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { ChevronRight } from 'lucide-react';
import { AddressForm } from '@duke-hq/google-address/components/AddressForm';
import type { AddressFormData, AddressSubmissionData } from '@duke-hq/google-address/types';
import { EMPLOYER_BUSINESS_TYPES } from '@duke-hq/shared-constants/employer';
import {
  FormContentContainer,
  FormFooterContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { testIds } from '../testIds';
import { EmployerBusinessFields } from '../components/EmployerBusinessFields';
import { EmployerContactFields } from '../components/EmployerContactFields';
import {
  EmployerBusinessDetailsSchema,
  type EmployerBusinessDetailsValues,
} from '@duke-hq/svc-api/admin/trpc/routers/workPlacements/employers/schemas';
import type { BusinessDetails, EmployerAddress } from './types';

const ADDRESS_FORM_ID = 'add-employer-address-form';

export function EmployerBusinessDetails({
  business,
  address,
  googleApiKey,
  onNext,
  onCancel,
  isSaving = false,
}: {
  business: BusinessDetails;
  address: EmployerAddress | null;
  googleApiKey: string;
  onNext: (business: BusinessDetails, address: AddressSubmissionData | null) => void;
  onCancel: () => void;
  isSaving?: boolean;
}): React.ReactElement {
  const isPresetType =
    business.businessType === '' ||
    EMPLOYER_BUSINESS_TYPES.some((type) => type === business.businessType);

  const methods = useForm<EmployerBusinessDetailsValues>({
    resolver: zodResolver(EmployerBusinessDetailsSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: business.name,
      abn: business.abn,
      businessType: isPresetType ? business.businessType : 'Other',
      businessTypeOther: isPresetType ? '' : business.businessType,
      contact: business.contact,
    },
  });
  const [initialAddress] = useState<Partial<AddressFormData>>(() =>
    address == null
      ? {}
      : {
          address: address.formattedAddress ?? '',
          formattedAddress: address.formattedAddress ?? undefined,
          addressStreetNumber: address.addressStreetNumber ?? undefined,
          addressStreetName: address.addressStreetName ?? undefined,
          addressStreetSubpremise: address.addressStreetSubpremise ?? undefined,
          addressLocality: address.addressLocality ?? undefined,
          addressState: address.addressState ?? undefined,
          addressPostcode: address.addressPostcode ?? undefined,
        },
  );

  const addrSubmittable = useRef(false);
  const addrHasUnappliedInput = useRef(false);
  const [addressError, setAddressError] = useState(false);

  const handleAddrSubmittableChange = (value: boolean) => {
    addrSubmittable.current = value;
    if (value) {
      setAddressError(false);
    }
  };

  const handleAddrHasUnappliedInputChange = (value: boolean) => {
    addrHasUnappliedInput.current = value;
    if (!value) {
      setAddressError(false);
    }
  };

  const proceed = (addressData: AddressSubmissionData | null) => {
    const values = methods.getValues();
    const businessType =
      values.businessType === 'Other' ? values.businessTypeOther.trim() : values.businessType;
    onNext(
      {
        name: values.name,
        abn: values.abn,
        businessType,
        contact: values.contact,
      },
      addressData,
    );
  };

  const handleNext = async () => {
    if (!(await methods.trigger())) {
      return;
    }
    if (addrSubmittable.current) {
      const form = document.getElementById(ADDRESS_FORM_ID);
      if (form instanceof HTMLFormElement) {
        form.requestSubmit();
      }
      return;
    }
    // Address is optional, but don't advance if the admin typed something the
    // form never captured — that text would be silently dropped otherwise.
    if (addrHasUnappliedInput.current) {
      setAddressError(true);
      return;
    }
    proceed(null);
  };

  return (
    <FormProvider {...methods}>
      <FormContentContainer>
        <VStack align="stretch" spacing={4} w="full" pb={4}>
          <EmployerBusinessFields />

          <FormControl isInvalid={addressError}>
            <FormLabel>
              <Trans>Address</Trans>
            </FormLabel>
            <AddressForm
              googleApiKey={googleApiKey}
              initialData={initialAddress}
              onSubmit={proceed}
              validationMode="relaxed"
              showPostalAddress={false}
              showTitle={false}
              formId={ADDRESS_FORM_ID}
              onSubmittableChange={handleAddrSubmittableChange}
              onHasUnappliedInputChange={handleAddrHasUnappliedInputChange}
            />
            <FormErrorMessage>
              <Trans>Select an address from the list, or enter it manually</Trans>
            </FormErrorMessage>
          </FormControl>

          <Text fontWeight="semibold" pt={2}>
            <Trans>Primary contact</Trans>
          </Text>
          <EmployerContactFields namePrefix="contact." />
        </VStack>
      </FormContentContainer>

      <FormFooterContainer>
        <Button
          variant="primary"
          onClick={() => void handleNext()}
          isLoading={isSaving}
          rightIcon={<ChevronRight />}
          data-testid={testIds.step1NextButton}
        >
          <Trans>Next</Trans>
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          <Trans>Cancel</Trans>
        </Button>
      </FormFooterContainer>
    </FormProvider>
  );
}
